
import { ArrowBack, Facebook, LinkedIn, Twitter, Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, Grid, IconButton, InputAdornment, Link, Paper, TextField, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import _ from "lodash";
import React, { ChangeEvent, FocusEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import logo from '../../assets/images/logo_navytext.png';
import { MfaSourceTypeEnum } from "../../common/enums/MfaSourceTypeEnum";
import { NavigationSource } from "../../common/enums/NavigationSource";
import { IAuthenticationResult } from "../../common/models/AuthenticationResult";
import { ILoginCustomer } from "../../common/models/LoginCustomer";
import { IeLynxUser } from "../../common/models/eLynxUser";
import { createUrlFromRouteDetail } from "../../common/utilities/CreateUrlFromRouteDetail";
import CustomerList from '../../components/customer-list/CustomerList';
import ForgotPasswordForm from '../../components/forgot-password-form/ForgotPasswordForm';
import { AppSettingsActionTypeEnum, useAppSettings } from '../../contexts/app-context/AppContext';
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { OfflineDetectActionType } from "../../contexts/offline-detect-context/OfflineDetectContextTypes";
import { UserState, UserStateActionTypeEnum, getUserStateKey, useUserContext } from '../../contexts/user-context/UserContext';
import msalAuthService, { MsalEventType } from "../../services/authentication/msal-auth-service/MsalAuthService";
import CustomerDataService from '../../services/customer-data-service/CustomerDataService';
import LoginDataService from '../../services/login-data-service/LoginDataService';
import restService, { RestError } from "../../services/rest-service/RestService";
import { StorageKeys } from "../../services/storage/StorageKeys";
import storageService from "../../services/storage/storage-service/StorageService";
import { IUIUserFavoriteRouteDetailModel } from "../../services/ui-personalization-data-service/Models";
import UIPersonalizationDataService from '../../services/ui-personalization-data-service/UIPersonalizationDataService';
import useLoginState, { LoginStepEnum } from './LoginViewState';
import LoginViewUtils, { LoginAuthResult, LoginChangeCustomerStorageKey, LoginLocationState } from './LoginViewUtils';
import { enc } from "crypto-js";
import useNavigateTo from '../../hooks/use-navigate-to/useNavigateTo';
import { eLynxObjectTypeEnum } from '../../common/enums/eLynxObjectType';
import { getGroupRoute } from '../../common/utilities/RouteHelpers';


export default function LoginView() {

    const { handleSubmit, register, formState: { errors }/*,clearErrors*/ } = useForm();
    const { t } = useTranslation();
    const [appSettings, dispatchAppSettings] = useAppSettings();
    const [userState, dispatchUserState] = useUserContext();
    const navigateTo = useNavigateTo();
    const location = useLocation();
    const [, setAppNotificationState] = useAppNotificationContext();
    const [offlineState, dispatchOfflineState] = useOfflineDetectContext();
    const { confirm } = useConfirmation();

    const autoLoginRef = useRef(false);
    const loginFromStateRef = useRef(false);

    const isPhone = isMobile && !isTablet;

    const { state,
        setUserName,
        setPassword,
        showPassword,
        //setFrameLoaded,
        rememberUser,
        setSSO,
        setStep,
        setCustomer,
        setValid,
        setForgotPassword,
        setLoginInfo,
        setCustomers,
        //setAuthenticating,
        setRedirectInProgress,
        setUseIDToken,
        setOtp,
    } = useLoginState();

    // for now make the login view always use the light theme.
    const theme = useMemo(
        () => {
            return createTheme(appSettings.themeSet["light"].main);
        },
        [appSettings.themeSet]
    );

    const email = register(
        "email", {
        pattern: {
            value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
            message: 'email is required'
        },
        value: state.userName, 
        required: state.useIDToken ? false : true
    }
    );

    let passwordField = useRef<HTMLInputElement | null>(null);
    let emailField = useRef<HTMLInputElement | null>(null);
    let otpField = useRef<HTMLInputElement | null>(null);

    let submitButton = useRef<HTMLButtonElement>(null);
    let selectRef = useRef<HTMLElement>(null);



    msalAuthService.registerEventCallback((event: MsalEventType) => {
        
        //debugger;
        //console.log(`msal: ${JSON.stringify(event)}`)
        //if impersonating, do not process any msal events
        if (storageService.session.get(StorageKeys.ImpersonatorUserID, '') !== '') {
            return;
        }
        

        if (event === MsalEventType.LOGIN_SUCCESS) {

            //setAuthenticating(true);
            userMsalAuthenticated();

        } else if (event === MsalEventType.HANDLE_REDIRECT_END /*&& !state.authenticating*/) {

            //console.log(`sso: ${state.isSSO} ${state.authenticating} ${state.userName}`)

            if (state.redirectInProgress) {

                setRedirectInProgress(false);
            }
            else if (!state.loginInfo && (state.userName ?? '') !== '') {

                _userNextOnline(true);
            }

            //console.log('Inside redirect end')
        } else if (event === MsalEventType.ACQUIRE_TOKEN_FAILURE ||
            event === MsalEventType.LOGIN_FAILURE ||
            event === MsalEventType.SSO_SILENT_FAILURE) {
            //setAuthenticating(false);
        } else if (event === MsalEventType.ACQUIRE_TOKEN_SUCCESS) {

            if (!state.customerID) {
                setStep(LoginStepEnum.SelectCustomer);
            } else if (!state.isSSO) {
                setSSO(true);
            }
        }
    });

    const handleUserFavorite = useCallback(async (userFavoriteString: string): Promise<{url: string | undefined, state: unknown}> => {

        if (userFavoriteString) {
            const favorite = LoginViewUtils.parseUserFavoriteFromHomeUrl(userFavoriteString);
            if (favorite) {
                const isValid = await LoginViewUtils.validateUserFavorite(favorite);

                if (!isValid) {

                    if ((await confirm({
                        dialogTitle: t("errors.favorites.invalidHomePageTitle"),
                        confirmationText: t("errors.favorites.notValid").replace("$desc", favorite.description),
                        showConfirmationText: true,
                        showYesButton: true,
                        noButtonText: t("buttons.okay"),
                    })) === "yes") {
                        await UIPersonalizationDataService.deleteUserFavorite(favorite.id);
                    }
                } else {
                    return createUrlFromRouteDetail(favorite.routeDetail as IUIUserFavoriteRouteDetailModel, NavigationSource.Favorite);
                }
            }
        }

        return {url: undefined, state: undefined};
    }, [
        confirm,
        t
    ]);

    const routeUser = useCallback(async (authenicationResult: IAuthenticationResult, overrideUrl?: string) => {

        let url: string | undefined;
        let navState: unknown;

        // when session expires, the user's current path (url) is stored so we can redirect there 
        // upon successful login
        if (!overrideUrl) {

            // the storgageService module is not always completely accessible when the login view is displayed
            // yet we must have the redirect restore path if it was set
            overrideUrl = window.sessionStorage.getItem(StorageKeys.RedirectRestorePath) ?? undefined;
            
            if (overrideUrl) {
                window.sessionStorage.removeItem(StorageKeys.RedirectRestorePath);
            }
        }


        if (overrideUrl) {
            url = overrideUrl;
        } else {

            let homeUrl: string | undefined;
            let homeState: unknown;
            if (authenicationResult.user && authenicationResult.user.homeUrl) {
                const fav = await handleUserFavorite(authenicationResult.user.homeUrl);
                homeUrl = fav.url;
                homeState = fav.state;
            }

            if (!homeUrl) {

                const defaultViews = await UIPersonalizationDataService.getUserDefaultViews();

                const globalGroupViewID = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                                                        !o.objectID &&
                                                        (o.user?.id === authenicationResult.user?.id || !o.user))?.viewID;

                const groupViewID = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                                                        o.objectID === authenicationResult.user?.startGroup?.id &&
                                                        (o.user?.id === authenicationResult.user?.id || !o.user))?.viewID;

                if (globalGroupViewID && authenicationResult.user?.startGroup?.id) {
                    homeUrl = getGroupRoute(globalGroupViewID, authenicationResult.user.startGroup.id);
                } else if (groupViewID && authenicationResult.user?.startGroup?.id) {
                    homeUrl = getGroupRoute(groupViewID, authenicationResult.user.startGroup.id);
                }
            }

            url = offlineState.offline && userState.defaultURL ? 
                    userState.defaultURL : 
                    LoginViewUtils.getDefaultURL(location.state as LoginLocationState,
                            authenicationResult.user as IeLynxUser,
                            homeUrl);

                navState = url === homeUrl
                    ? homeState
                    : undefined

            dispatchUserState({
                type: UserStateActionTypeEnum.DefaultUrlChanged,
                defaultUrl: url
            });

        }

        navigateTo(url, {state: navState, replace: true});
    }, [
        offlineState.offline,
        handleUserFavorite,
        location,
        navigateTo,
        dispatchUserState,
        userState.defaultURL
    ]);

    const _updateSettings = useCallback(async (userState: UserState) => {


        if (userState.authenticated) {

            // update app settings state
            dispatchUserState({
                type: UserStateActionTypeEnum.UserState,
                userState: userState
            });

            dispatchAppSettings({
                type: AppSettingsActionTypeEnum.ThemeSetMode, mode:
                    (userState.userSettings?.theme ?? 'light') === 'light' ? 'light' : 'dark'
            });
        }
    },
        [
            dispatchAppSettings,
            dispatchUserState
        ]
    );

    const userMsalAuthenticated = useCallback(async () => {

        try {
            // user has been authenticated thru msal, now begin our token authentication
            let result = await LoginViewUtils.authenticate(false,
                false,
                [],
                userState,
                undefined,
                true);

            const loginName = result.authenticationResult.user?.loginName ?? storageService.local.get(StorageKeys.LastUserName, "");

            // login data and state needed for the next steps
            const loginData = await LoginDataService.getLoginInfo(loginName);
            setLoginInfo(loginData);
            setUserName(loginData.externalLoginName ?? loginData.loginName);
            setCustomers(result.authenticationResult.customers);

            // elynx user is in a single customer
            if (result?.authenticationResult.customerID) {
                // finalize authentication for this elynx user for the specific customer
                result = await LoginViewUtils.authenticate(false,
                    false,
                    result.authenticationResult.customers,
                    userState,
                    result.authenticationResult.customerID,
                    true);

                const customer = result.authenticationResult.customers.find(o => o.customerID === +result.authenticationResult.customerID);
                setCustomer(customer as ILoginCustomer);
                setSSO(true);
            }

            _updateSettings(result.userState);

            // user is in one customer and authenticated, redirect to their home page
            if (result.userState?.authenticated) {

                await routeUser(result.authenticationResult);

            } else {
                // user must pick a customer
                setStep(LoginStepEnum.SelectCustomer);
                //setAuthenticating(false);
            }

        } catch (error) {
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                error: error as RestError,
                show: true
            });
        }
    }, [
        userState,
        routeUser,
        setStep,
        //setAuthenticating,
        setCustomers,
        setCustomer,
        setLoginInfo,
        setUserName,
        setSSO,
        setAppNotificationState,
        _updateSettings
    ]);

    const onNext = () => {

        switch (state.step) {
            case LoginStepEnum.UserName:
                _onUserNext();
                break;

            case LoginStepEnum.Password:
                _onPasswordNext();
                break;

            case LoginStepEnum.SelectCustomer:
            case LoginStepEnum.OtpChallenge:
                _onLogin(state.customerID);
                break;

            default:
                throw Error(`step ${state.step} not implememented`);
        }
    };

    // use a generic click handler that will dispatch correct type
    // to keep code DRY
    const onLinkClicked = async (action: string) => {

        switch (action) {
            case "back":
                setStep(LoginStepEnum.UserName);
                break;

            case "forgotPassword":
                setForgotPassword(true);
                break;

            case "sendOtpEmail":
                onResendOtpEmail();
                break;

            default:
                throw Error(`onLinkClicked called with invalid action: ${action}`);
        }
    };

    const _authenticate = async (customerID: number | null, sso: boolean, useIDToken?: boolean): Promise<IAuthenticationResult> => {

        const result = await LoginViewUtils.authenticate(true,
            useIDToken ?? false,
            state.customers,
            userState,
            customerID ?? undefined,
            sso,
            state.userName,
            state.userPassword,
            state.otp,
            state.mfaSource
        );

        const authResult = result.authenticationResult;

        if (authResult.customers) {
            setCustomers(authResult?.customers);
        }

        if (customerID) {
            const customerList = authResult?.customers ?? state.customers;
            const customer = customerList.find(o => o.customerID === +authResult.customerID);
            setCustomer(customer as ILoginCustomer);
            setSSO(sso);
        }

        _updateSettings(result.userState);

        return result.authenticationResult;
    };

    const _onUserNext = async () => {

        if (offlineState.offline) {
            _userNextOffline();
        } else {
            await _userNextOnline();
        }

    };

    const _userNextOnline = async (onlySSO?: boolean) => {

        try {
            setValid(false);

            const loginData = await LoginDataService.getLoginInfo(state.userName);

            setLoginInfo(loginData);

            if (loginData?.allowSSO) {

                // store the actual elynx login name for the user
                setUserName(state.userName);

                const result = await _authenticate(null, true);

                if (result.customers.length) {
                    setStep(LoginStepEnum.SelectCustomer);
                }
            } else {
                // if (!onlySSO) {
                    setStep(LoginStepEnum.Password);
                // }
            }

        } catch (error) {

            // User Story #44287: React App - Login Page - Error Handling update,
            // go ahead and allow user to enter password before showing error
            setStep(LoginStepEnum.Password);
        }
    };

    const _userNextOffline = () => {

        if (userState.customer &&
            userState.customer?.id === state.customerID &&
            userState.user &&
            userState.user?.loginName === state.userName &&
            userState.defaultURL) {

            setCustomer({
                customerID: userState.customer?.id,
                customerName: userState.customer?.displayText ?? '',
                mobileAccess: userState.user.mobileAccessEnabled ?? false,
                userID: userState.user?.id
            });

            dispatchUserState({
                type: UserStateActionTypeEnum.Authenticated,
                authenticated: true
            });

            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Info,
                message: t("offlineDetect.isOffline"),
                hideContactInfo: true,
                show: true
            });

            navigateTo(userState.defaultURL, {replace: true});
        } else {
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                message: t('errors.authentication.loginFailed'),
                hideContactInfo: false,
                show: true
            });
        }
    };

    const _onPasswordNext = async () => {

        try {
            setValid(false);

            // User Story #44287: React App - Login Page - Error Handling update,
            // user was allowed to enter password before, but now show the error
            if (!state.loginInfo) {
                throw new Error();
            }

            const result = await _authenticate(null, false);

            if (result?.customers?.length > 1) {
                setStep(LoginStepEnum.SelectCustomer);
            } else if (result?.customers?.length === 1 && result.customers[0].showOTP) {
                setCustomer(result.customers[0]);
                setStep(LoginStepEnum.OtpChallenge);
            } else {

                const customer = result.customers?.find(o => o.customerID === +result.customerID);
                if (customer) {
                    setCustomer(customer);
                }

                await _onLogin(result?.customerID);
            }

        } catch (error) {

            passwordField?.current?.focus();
            passwordField?.current?.select();

            if (!state.loginInfo) {

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    message: t('errors.authentication.loginFailed'),
                    show: true
                });

            } else {

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    error: error as RestError,
                    show: true
                });
            }
        }
    };

    const _onLogin = async (customerID: number) => {

        try {
            setValid(false);

            let authenticationResult: IAuthenticationResult;
            if ((state.loginInfo?.allowSSO && state.customers.length) || (state.useIDToken && state.isSSO)) {
                authenticationResult = await _authenticate(customerID, true, state.useIDToken);
            } else {
                authenticationResult = await _authenticate(customerID, false, state.useIDToken);
            }

            await routeUser(authenticationResult);

        } catch (error) {
            const restError = error as RestError;

            if (restError?.response?.data?.errorCode?.toLowerCase() === "invalidotp") {
                // Stay on the current page to allow for another OTP entry attempt
                setOtp("");
                if (otpField?.current) {
                    otpField.current.value = "";
                    otpField.current.focus();
                }
            } else {
                setStep(LoginStepEnum.UserName);
            }

            emailField?.current?.focus();

            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                error: error as RestError,
                show: true
            });
        }
    };


    // const onFrameLoaded = (event: React.SyntheticEvent<HTMLIFrameElement, Event>) => {
    //     setFrameLoaded(true);
    // };

    // use a generic input changed handler that will dispatch correct type
    // to keep code DRY
    const onInputChanged = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        _updateCredentials(event.target.name, event.target.value);
    };

    const onFocus = (event: FocusEvent<HTMLInputElement>) => {
        _updateCredentials(event.target.name, event.target.value);
    };

    const _updateCredentials = (name: string, value: string) => {
        name === "email" ?
            setUserName(value.trim()) :
            name === "otp" ?
                setOtp(value.trim()) :
                setPassword(value.trim());

        if (errors.email) {
            setValid(false);
        }
    };

    const onRememberUserChanged = (event: React.ChangeEvent<{}>, checked: boolean) => {
        rememberUser(checked);
    };

    const onShowPassword = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        showPassword(!state.showPassword);
    };

    const onCustomerSelected = async (selectedCustomer: number) => {
        const customer = state.customers.find(o => o.customerID === selectedCustomer);
        if (customer) {
            setCustomer(customer);
        }
    };

    const onResendOtpEmail = useCallback(() => {
        LoginDataService.sendOtpEmail(state.customerID, state.userName);
    }, [state.customerID, state.userName]);

    // support center login, impersonation
    useEffect(() => {

        // move support center login to the LoginViewService
        const supportCenterLogin = async (autoLoginToken: string) => {

            //unencode auto-login token
            //token format: "customerID:userName:passwordHash", Utf8 encoded then converted to a Base64 string
            const encodedBytes = enc.Base64.parse(autoLoginToken);
            const tokenText = enc.Utf8.stringify(encodedBytes);
            const tokenParts = tokenText.split(":");
            const customerID = +tokenParts[0];
            const userName = tokenParts[1];
            const password = tokenParts[2];

            let impersonatingGlobalLoginID: number | undefined;
            if (tokenParts.length > 3) {
                impersonatingGlobalLoginID = +tokenParts[3];
                const impersonatorInfo = await LoginDataService.getImpersonatingUserInfo(customerID, impersonatingGlobalLoginID);
                restService.setImpersonator(impersonatorInfo.userID, impersonatorInfo.userLogin);

                storageService.session.set(StorageKeys.ImpersonatingGlobalLoginID, impersonatingGlobalLoginID);
                storageService.session.set(StorageKeys.ImpersonatorUserID, impersonatorInfo.userID);
                storageService.session.set(StorageKeys.ImpersonatorUserLogin, impersonatorInfo.userLogin);
            }

            try {
                let result: LoginAuthResult | undefined = undefined;
    
                // first attempt without signing in the user again
                result = await LoginViewUtils.authenticate(
                    false,
                    false,
                    [],
                    userState,
                    undefined,
                    false,
                    userName,
                    password,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    true,
                    impersonatingGlobalLoginID
                );

                // authentication may have failed, possibly from expired tokens.  If authentication fails
                // it is likely the msal service will redirect away from here, so processing should pick
                // back up in the event handler
                if (result) {

                    const customer = result.authenticationResult.customers.find(o => o.customerID === customerID);
                    setCustomer(customer as ILoginCustomer);

                    result = await LoginViewUtils.authenticate(
                        false,
                        false,
                        result.authenticationResult.customers,
                        userState,
                        customerID,
                        false,
                        userName,
                        password,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        true,
                        impersonatingGlobalLoginID);

                    _updateSettings(result.userState);

                    await routeUser(result.authenticationResult);
                }

            } catch (error) {
                // there is an issue with sso, so clear the flag and display the error
                setSSO(false);

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    error: error as RestError,
                    show: true
                });
            }
        };


        if(!autoLoginRef.current) {

            if (location.search.length > 0) {

                const params = new URLSearchParams(location.search);

                // support center token
                if (params.has("autoLoginToken")) {

                    const autoLoginToken = params.get("autoLoginToken") ?? "";

                    autoLoginRef.current = true;

                    setTimeout(() => {
                        supportCenterLogin(autoLoginToken);
                    }, 500);
                }
            }
        }
    }, [
        _updateSettings,
        dispatchUserState,
        location,
        routeUser,
        setAppNotificationState,
        setCustomer,
        setSSO,
        state.isSSO,
        userState
    ]);

    useEffect(() => {

        const loginUser = async (loginName: string, customerID: number, userState: UserState) => {

            try {
                
                const result = await LoginViewUtils.authenticateFromAuthMsg(userState, customerID, loginName);

                // user is in one customer and authenticated, redirect to their home page
                if (result && result.userState?.authenticated) {

                    _updateSettings(result.userState);

                    const restoreUrl = window.localStorage.getItem(StorageKeys.RedirectRestorePath) ?? undefined;
            
                    if (restoreUrl) {
                        window.localStorage.removeItem(StorageKeys.RedirectRestorePath);
                    }                    

                    //console.log(`Login user from state: ${restoreUrl}`);

                    const impersonatorUserID: string = storageService.session.get(StorageKeys.ImpersonatorUserID, '');
                    const impersonatorUserLogin: string  = storageService.session.get(StorageKeys.ImpersonatorUserID, '');
                    if (impersonatorUserID !== '' && impersonatorUserLogin !== '') {
                        restService.setImpersonator(+impersonatorUserID, impersonatorUserLogin);
                    }
                    await routeUser(result.authenticationResult, restoreUrl);
                }

            } catch (error) {
                // nothing to do here. the assumption is the user cannot automatically login
                // so normal processing will occur
            }
        }


        // do not interrupt the SSO flow and unsure we have the lastCustomerID and login
        if (!state.isSSO && !loginFromStateRef.current && state.lastCustomerID && state.userName) {

            //debugger;

            // get the user state stored in local storage to determine if the user has previously 
            // been authenticated
            const localUserState = storageService.local.get<UserState | undefined>(getUserStateKey(true), undefined);
            if (localUserState?.authenticated && localUserState.startGroupID && 
                    storageService.session.get(StorageKeys.ImpersonatorUserID, '') === '') {

                loginUser(state.userName, state.lastCustomerID, localUserState);
            }
            loginFromStateRef.current = true;
        }
    }, [
        state.isSSO,
        state.userName,
        state.lastCustomerID,
        userState,
        routeUser,
        _updateSettings
    ]);

    // login from scadalynx redirect
    useEffect(() => {

        const autoLogin = async (token: string, customerID: number, url: string | undefined | null) => {

            try {
                let result: LoginAuthResult | undefined = undefined;

                // first attempt without signing in the user again
                result = await LoginViewUtils.authenticate(false,
                    true,
                    [],
                    userState,
                    //customerID,
                    undefined,
                    false,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    token);

                // authentication may have failed, possibly from expired tokens.  If authentication fails
                // it is likely the msal service will redirect away from here, so processing should pick
                // back up in the event handler
                if (result) {

                    const customer = result.authenticationResult.customers.find(o => o.customerID === customerID);
                    setCustomer(customer as ILoginCustomer);

                    result = await LoginViewUtils.authenticate(false,
                        true,
                        result.authenticationResult.customers,
                        userState,
                        customerID,
                        false,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        token);

                    _updateSettings(result.userState);

                    await routeUser(result.authenticationResult, url ?? undefined);
                }
            } catch (error) {
                // there is an issue with sso, so clear the flag and display the error
                setSSO(false);

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    error: error as RestError,
                    show: true
                });
            }
        };

        if (!state.useIDToken && !autoLoginRef.current) {

            if (location.search.length > 0) {

                const params = new URLSearchParams(location.search);
                if (params.has("idt") && params.has("cid")) {
                    const autoLoginToken = params.get("idt");
                    const customerID = params.get("cid");
                    const url = params.get("q");
                    if (autoLoginToken && customerID) {
                        setUseIDToken(true);
                        autoLoginRef.current = true;

                        setTimeout(() => {
                            autoLogin(autoLoginToken, +customerID, url?.replace(/'/g, '').replace(/~/g, '&'));
                        }, 500);
                        
                    }
                }
            }
        }
    }, [
        state.useIDToken,
        location.search,
        setUseIDToken,
        _updateSettings,
        routeUser,
        setAppNotificationState,
        setSSO,
        setCustomer,
        userState
    ]);

    // handle 'sso' login when the user has already been authenticated with sso and an app refresh has occurred
    useEffect(() => {

        const doSSO = async () => {

            try {
                let result: LoginAuthResult | undefined = undefined;

                const customerID = state.customerID === 0 ? undefined : state.customerID;
                const customerList = await CustomerDataService.getCustomerList();

                // first attempt without signing in the user again
                result = await LoginViewUtils.authenticate(false,
                    state.useIDToken,
                    customerList,
                    userState,
                    customerID,
                    true);

                // authentication may have failed, possibly from expired tokens.  If authentication fails
                // it is likely the msal service will redirect away from here, so processing should pick
                // back up in the event handler
                if (result) {

                    _updateSettings(result.userState);

                    await routeUser(result.authenticationResult);
                }

            } catch (error) {
                // there is an issue with sso, so clear the flag and display the error
                setSSO(false);

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    error: error as RestError,
                    show: true
                });
            }
        };


        if (state.isSSO && /*state.customerID &&*/ !state.useIDToken && !autoLoginRef.current) {
            console.log('doSSO');

            if (!state.customerID) {

                setStep(LoginStepEnum.SelectCustomer);
                setSSO(false);

                // since sso is now false, prevent login from state to execute
                loginFromStateRef.current = true;

            } else {

                // if offline, check user state... move user state to local storage!
                if (offlineState.offline &&
                    userState.authenticated &&
                    userState.customer &&
                    userState.user &&
                    userState.defaultURL) {

                    navigateTo(userState.defaultURL, {replace: true});
                    //history.replace('default/summary-listing/devices?groupID=307');;
                } else if (!autoLoginRef.current) {
                    doSSO();
                }
            }
        }
    }, [
        navigateTo,
        state.isSSO,
        setSSO,
        state.customerID,
        userState,
        routeUser,
        setAppNotificationState,
        offlineState.offline,
        state.useIDToken,
        _updateSettings,
        setStep,
    ]);


    // change customer from outside of this view
    useEffect(() => {

        const changeCustomer = async () => {

            let customers: ILoginCustomer[];
            if (offlineState.offline) {
                customers = await CustomerDataService.getCustomerList();
            } else {

                // login data and state needed for the next steps
                customers = await CustomerDataService.getCustomerList(true);
            }

            if (!_.isEqual(state.customers.map(o => o.customerID),
                customers.map(o => o.customerID))) {
                setCustomers(customers);
            }

            dispatchUserState({
                type: UserStateActionTypeEnum.Authenticated,
                authenticated: false
            });

            dispatchOfflineState({
                type: OfflineDetectActionType.OfflineDataLoaded,
                payload: false
            });

            setUseIDToken(false);

            setStep(LoginStepEnum.SelectCustomer);
        };

        // change customer initiated from the navbar
        if (storageService.session.get(LoginChangeCustomerStorageKey, false)) {
            storageService.session.remove(LoginChangeCustomerStorageKey);
            changeCustomer();
        }

    }, [
        dispatchUserState,
        dispatchOfflineState,
        state.useIDToken,
        setStep,
        setCustomers,
        setUseIDToken,
        userState,
        offlineState.offline,
        state.customers
    ]);
    
    

    // when progressing thru the login steps, we want to be able to set the focus on an element
    useEffect(() => {


        switch (state.step) {
            case LoginStepEnum.UserName:
                emailField?.current?.focus();
                break;

            case LoginStepEnum.Password:
                passwordField?.current?.focus();
                break;

            case LoginStepEnum.SelectCustomer:
                if (state.customerID) {
                    submitButton?.current?.focus();
                } else {
                    selectRef?.current?.focus();
                }

                break;
        }


    }, [
        state.step,
        state.customerID,
    ]);

    // select customer, make sure customer list has been loaded
    useEffect(() => {

        const loadCustomers = async () => {

            let customers = await CustomerDataService.getCustomerList(true);

            setCustomers(customers);

        };

        if (state.step === LoginStepEnum.SelectCustomer && state.customers.length === 0) {
            loadCustomers();

        }

    }, [
        state.isSSO,
        state.step,
        state.customers,
        setCustomers,
    ]);

    //console.log(`#### Rendering LoginView: state=${JSON.stringify(state)}`);

    return (
        <ThemeProvider theme={theme}>
            <Grid container component="main" sx={{ height: '100vh', backgroundColor: '#505e78' }}>

                <Grid
                    sx={{ 
                        display: (state.isSSO && !state.useIDToken) || 
                                     //autoLoginRef.current || 
                                    //  (loginFromStateRef.current && state.customerID !== 0) ? 
                                    autoLoginRef.current ? 
                                        'flex' : 
                                        'none', 
                        padding: theme.spacing(5), 
                        width: '100%' 
                    }}
                    component={Paper}
                    elevation={6}
                    square
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12
                    }}>

                    <Container sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: theme.spacing('auto', 'auto'),
                        padding: theme.spacing(1)
                    }}>

                        <img style={{ width: '70%', maxWidth: '380px' }}
                            src={logo}
                            alt='eLynx logo' />

                        <CircularProgress
                            sx={{
                                color: theme.palette.primary.main,
                                margin: 'auto',
                                marginBottom: theme.spacing(2)
                            }} />

                        <Typography component="label" sx={{ textAlign: 'center' }}>
                            {
                                
                                    t("login.signingIn") + " " + state.userName
                            }
                        </Typography>

                    </Container>
                </Grid>

                <Grid
                    sx={{ 
                        display: (state.isSSO && !state.useIDToken) || 
                                     autoLoginRef.current || 
                                     //(loginFromStateRef.current && state.customerID !== 0) ? 
                                    autoLoginRef.current  ? 
                                        'none' : 
                                        'flex', 
                                        flexDirection: 'column' 
                    }}
                    component={Paper}
                    elevation={6}
                    square
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 5
                    }}>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: theme.spacing('auto', 'auto'),
                        padding: theme.spacing(1)
                    }}>

                        <img
                            style={{ 
                                width: '70%', 
                                maxWidth: '380px' 
                            }}
                            src={logo}
                            alt='eLynx logo' />

                        <form style={{ 
                            display: state.forgotPassword ? 
                                'none' : 
                                'block', 
                                width: '100%', 
                                marginTop: theme.spacing(1) 
                            }}
                            noValidate
                            onSubmit={
                                handleSubmit((data) => {
                                        onNext();
                                },
                                (e) => {
                                    setValid(false);
                                })}
                        >

                            {/* user login/email step */}
                            <Box sx={{ 
                                display: state.step === LoginStepEnum.UserName ? 
                                'flex' : 
                                'none', 
                                flexDirection: 'column' 
                            }}>

                                <TextField
                                    id="email"
                                    name={email.name}
                                    data-testid="email"
                                    inputRef={(e) => {
                                        email.ref(e);
                                        emailField.current = e;
                                    }}
                                    variant='filled'
                                    error={errors.email ? true : false}
                                    //helperText={errors.email ? 'A valid email is required' : ''}
                                    disabled={state.isSSO}
                                    defaultValue={state.userName}
                                    margin="normal"
                                    required
                                    fullWidth
                                    label={t("login.emailAddress")}
                                    autoComplete="email"
                                    autoFocus
                                    onFocus={onFocus}
                                    onChange={(e) => {
                                        email.onChange(e);
                                        onInputChanged(e);
                                    }}
                                    onBlur={email.onBlur} />

                                <FormControlLabel
                                    color="primary"
                                    disabled={state.isSSO}
                                    control={
                                        <Checkbox
                                            value="remember"
                                            //color="secondary"
                                            checked={state.rememberUser}
                                            slotProps={{
                                                input: {
                                                    name: "rememberme",
                                                    role: "checkbox"
                                                }
                                            }} />
                                    }
                                    label={t("login.rememberMe") as string}
                                    onChange={onRememberUserChanged}
                                />
                            </Box>

                            {/* password step */}
                            <Box sx={{ display: state.step === LoginStepEnum.Password ? 
                                            'flex' : 
                                            'none', 
                                            flexDirection: 'column' 
                            }}>
                                <IconButton
                                    sx={{ alignSelf: 'flex-start' }}
                                    size="small"
                                    aria-label="back to user name"
                                    component="span"
                                    onClick={() => onLinkClicked("back")}>
                                    <ArrowBack />
                                    {state.userName}
                                </IconButton>

                                <TextField
                                    id="password"
                                    name="password"
                                    data-testid="password"
                                    inputRef={passwordField}
                                    type={state.showPassword ? 'text' : 'password'}
                                    defaultValue={state.userPassword}
                                    autoComplete="new-password"
                                    margin="normal"
                                    required
                                    fullWidth
                                    variant='filled'
                                    label={t("login.password")}
                                    onChange={onInputChanged}
                                    onFocus={onFocus}
                                    slotProps={{
                                        input: {
                                            endAdornment:
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={onShowPassword} >
                                                        {state.showPassword ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                        }
                                    }} />
                                <Link href="#" variant="body2" color="textPrimary"
                                    aria-label="forgot password"
                                    onClick={() => onLinkClicked("forgotPassword")}>
                                    {t("login.forgotPassword")}
                                </Link>
                            </Box>

                            {/* select customer step */}
                            <Box sx={{ 
                                    display: state.step === LoginStepEnum.SelectCustomer ? 
                                        'flex' : 
                                        'none', 
                                        flexDirection: 'column' 
                            }}>
                                {state.customers.length ?
                                    <CustomerList value={state.customerID}
                                        selectRef={selectRef}
                                        customers={state.customers}
                                        onSelected={onCustomerSelected} /> :
                                    null
                                }
                            </Box>

                            <Box sx={{ 
                                display: (state.step !== LoginStepEnum.SelectCustomer ||
                                             !state.showOtp) && 
                                             state.step !== LoginStepEnum.OtpChallenge ? 
                                             'none' : 
                                             'flex', 
                                             flexDirection: 'column' 
                                }
                            }>
                                <TextField
                                    id="otp"
                                    name="otp"
                                    data-testid="otp"
                                    inputRef={otpField}
                                    margin="normal"
                                    required
                                    fullWidth
                                    variant='filled'
                                    label={t("login.otp")}
                                    onChange={onInputChanged}
                                    type="number"
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: 6
                                        }
                                    }}
                                />
                            </Box>

                            <Box sx={{
                                display: ((state.step !== LoginStepEnum.SelectCustomer || !state.showOtp) && 
                                            state.step !== LoginStepEnum.OtpChallenge) || 
                                            state.mfaSource === MfaSourceTypeEnum.Authenticator ? 
                                            'none' : 
                                            'flex',
                                whiteSpace: 'normal',
                                fontSize: 'small',
                                marginLeft: theme.spacing(6),
                                marginRight: theme.spacing(6),
                                flexWrap: "wrap"
                            }}>
                                <Box>
                                    If you did not receive your OTP via email and would like to have a new one sent,&nbsp;
                                </Box>
                                <Link onClick={() => onLinkClicked("sendOtpEmail")} aria-label="send one time password via email" color="textPrimary" href="#" variant='inherit' sx={{
                                    fontWeight: 600
                                }}>
                                    Click Here&nbsp;
                                </Link>
                                <Box>
                                    This will invalidate any previous OTP.
                                </Box>
                            </Box>

                            <Button
                                name="submit"
                                ref={submitButton}
                                disabled={!state.valid || (state.isSSO && !state.useIDToken)}
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="secondary"
                                sx={{ margin: theme.spacing(3, 0, 2) }}>
                                {
                                    state.step === LoginStepEnum.Password && !state.loginInfo?.showCustomers ?
                                        t("buttons.signIn") :
                                        state.step !== LoginStepEnum.SelectCustomer && state.step !== LoginStepEnum.OtpChallenge ?
                                            t("buttons.next") :
                                            t("buttons.signIn")}
                            </Button>

                        </form>

                        {/* User Story 54019: Login Screen - Add disclaimer language */}
                        {/* {((state.step === LoginStepEnum.Password && !state.loginInfo?.showCustomers) || state.step === LoginStepEnum.SelectCustomer) && */}
                            <Box sx={{ whiteSpace: 'normal', fontSize: 'small', marginLeft: theme.spacing(6), marginRight: theme.spacing(6) }}>
                                By signing in, you agree to our&nbsp;
                                <Link aria-label="terms and conditions" color="textPrimary" href="https://www.elynxtech.com/terms-and-conditions" target="_blank" variant='inherit' sx={{
                                    fontWeight: 600
                                }}>
                                    Terms and Conditions&nbsp;
                                </Link>
                                and&nbsp;
                                <Link aria-label="privacy policy" color="textPrimary" href="https://www.iubenda.com/privacy-policy/70098978" target="_blank" variant='inherit' sx={{
                                    fontWeight: 600
                                }}>
                                    Privacy Policy
                                </Link>
                            </Box>
                        {/* } */}


                        {state.forgotPassword ?
                            <ForgotPasswordForm userName={state.userName}
                                visible={true}
                                onClose={() => setForgotPassword(false)} />
                            : null
                        }

                    </Box>

                    <Box sx={{
                        margin: theme.spacing(1),
                        display: 'flex',
                        '& a:nth-of-type(2)': {
                            marginLeft: 'auto'
                        }
                    }}>

                        <Link sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                            aria-label="visit eLynx support" color="textPrimary" href="http://elynxtech.zendesk.com/home" target="_blank" variant="h5">
                            {t("elynxSupport")}
                        </Link>

                        <Link sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                            aria-label="visit eLynx on Facebook" href="https://www.facebook.com/elynxtech" target="_blank">
                            <IconButton component="span">
                                <Facebook />
                            </IconButton>
                        </Link>

                        <Link sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                            aria-label="visit eLynx on Twitter" href="https://twitter.com/elynxtech" target="_blank">
                            <IconButton component="span">
                                <Twitter />
                            </IconButton>
                        </Link>

                        <Link sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                            aria-label="visit eLynx on LinkedIn" href="https://www.linkedin.com/company/elynx-technologies" target="_blank">
                            <IconButton component="span">
                                <LinkedIn />
                            </IconButton>
                        </Link>
                    </Box>
                </Grid>

                <Grid
                    sx={{
                        display: !state.isSSO && !state.useIDToken && !autoLoginRef.current ? 'flex' :
                         (state.isSSO && !state.useIDToken) || 
                            autoLoginRef.current || 
                            loginFromStateRef.current ? 
                            'none' : 'flex',

                        // display: (state.isSSO && !state.useIDToken) || 
                        //     autoLoginRef.current || 
                        //     loginFromStateRef.current ? 
                        //     'none' : 'flex',
                        flexDirection: 'column'
                    }}
                    size={{
                        xs: false,
                        sm: 6,
                        md: 7
                    }}>

                    {/* {!isPhone && 
                        !state.frameLoaded &&
                        <Box sx={{
                            margin: 'auto',
                            width: '60px',
                            height: '60px',
                            display: 'flex',
                            flexDirection: 'column'
                        }} >
                            <CircularProgress sx={{ color: 'white', margin: 'auto' }} />
                        </Box>
                    } */}
                    {!isPhone &&

                        <iframe 
                            //onLoad={onFrameLoaded}
                            title="eLynx news"
                            //frameBorder="0"
                            src="https://www.elynxtech.com/news-you-can-use"
                            style={{
                                height: "100%",
                                width: "100%",
                                border: "none",
                                //display: state.frameLoaded ? "" : "none"
                            }} />
                    }
                </Grid>

            </Grid>
        </ThemeProvider>
    );
}
